import {
	Divider,
	Headline,
	Layout,
	ProgressIndicator,
	Tag,
	TagGroup,
	Text,
	TextLink,
} from '@oneaudi/unified-web-alpha-components';
import { useCallback, useEffect, useState } from 'react';
import { useAppState } from '~/app/contexts/app-context';
import { useI18n } from '~/app/i18n/i18n';
import { SaveConfiguration } from '~/app/pages/summary/components/save-configuration/components/save-configuration';
import {
	StyledAudicodeWrapper,
	StyledItemsContainer,
	StyledMainContainer,
	StyledMyAudiWrapper,
} from '~/app/pages/summary/dialogs/audi-code/styles/audi-code.dialog-style';
import { DialogProps } from '~/app/pages/summary/dialogs/dialog-props';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction, LayerType } from '~/app/tracking/feld-m-types';
import { Dialog } from '~/components/dialog';
import { Disclaimer } from '~/components/disclaimer/disclaimer';

export function AudiCodeDialog({ open, onClose }: DialogProps) {
	useEffect(() => {
		if (open) {
			appTrackingService.updateLayerComponent(LayerType.SaveConfigurationLayer, 'open');
			appTrackingService.viewChangeEvent({
				eventName: `configurator - ${LayerType.SaveConfigurationLayer}`,
				componentName: 'save-configuration-layer',
			});
		}
	}, [open]);
	return (
		<Dialog open={open} onCloseButtonClick={onClose}>
			<AudiCodeDialogContent />
		</Dialog>
	);
}

function AudiCodeDialogContent() {
	const { i18n } = useI18n();
	const { isLoggedIn, audiCode } = useAppState();
	const [hasCopied, setHasCopied] = useState(false);

	const audiCodeString = audiCode?.data.id ?? '';
	const loading = audiCode?.loading ?? '';
	const href = audiCode?.data?.urls?.entry ?? '';
	const myAudiUrlConfigured = Boolean(i18n('ngc.saveConfiguration.myAudi.wishlist.url'));

	const onAudiCodeCopy = useCallback(() => {
		navigator.clipboard.writeText(audiCodeString).catch(console.error);
		setHasCopied(true);
		appTrackingService.summaryClickEvent({
			eventAction: EventAction.Content,
			eventName: `configurator (save configuration) - click to copy audi code`,
			componentName: 'configurator-save-configuration',
			label: i18n('ngc.audiCode.copy'),
			elementName: 'text link',
		});
	}, [audiCodeString, i18n]);

	const onDirectLinkClick = useCallback(() => {
		appTrackingService.summaryClickEvent({
			eventAction: EventAction.InternalLink,
			eventName: `configurator (save configuration) - click on Audi code open link`,
			componentName: 'configurator-save-configuration',
			label: href,
			elementName: 'text link',
			targetURL: href,
		});
	}, [href]);

	useEffect(() => {
		setTimeout(() => {
			setHasCopied(false);
		}, 2000);
	}, [hasCopied]);

	return (
		<StyledMainContainer>
			<StyledAudicodeWrapper>
				<Layout direction={'column'} rowGap={'var(--spacing-relative-xs)'}>
					{}
					<div tabIndex={0}>
						<Headline tag="h2" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
							{i18n('ngc.audiCode.yourCode')}
						</Headline>

						<Text
							className="subtitle"
							variant={{
								type: 'copy',
								order: '2',
								style: 'normal',
							}}>
							{i18n('ngc.audiCode.explanation')}
						</Text>
					</div>

					<StyledItemsContainer
						as={Layout}
						direction="row"
						rowGap="var(--spacing-relative-sm)"
						columnGap="var(--spacing-relative-sm)">
						{loading && !audiCode ? (
							<ProgressIndicator variant={'spinner'} type={'infinite'} />
						) : (
							<div tabIndex={0} className="audicode-label-group" aria-label={audiCodeString}>
								<TagGroup className="audicode-label-group" size="md">
									{audiCodeString.split('').map((char, index) => (
										<Tag
											key={index}
											className="audicode-label"
											label={char}
											size="md"
											variant="read-only"
										/>
									))}
								</TagGroup>
							</div>
						)}
						<TextLink
							icon={'copy'}
							aria-label={i18n('ngc.a11y.audicode.copy')}
							iconPosition={'leading'}
							variant={'standalone'}
							className="info-btn"
							underline={false}
							size={'sm'}
							onClick={onAudiCodeCopy}>
							{hasCopied ? `${i18n('ngc.common.copied')}!` : i18n('ngc.audiCode.copy')}
						</TextLink>
					</StyledItemsContainer>
				</Layout>
			</StyledAudicodeWrapper>
			<Disclaimer
				type={'audicode'}
				headline={i18n('ngc.saveConfiguration.disclaimer.title')}
				content={i18n('ngc.saveConfiguration.disclaimer.message')}
				link={href}
				onLinkClick={onDirectLinkClick}
			/>

			{myAudiUrlConfigured && (
				<>
					<Divider />
					<StyledMyAudiWrapper>
						<Layout direction={'column'} rowGap={'var(--spacing-relative-lg)'}>
							<Layout direction={'column'} rowGap={'var(--spacing-relative-md)'}>
								<Layout direction={'column'} rowGap={'var(--spacing-relative-xs)'}>
									{}
									<div tabIndex={0}>
										<Headline tag="h2" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
											{i18n('ngc.saveConfiguration.myAudi.headline')}
										</Headline>
										<Text
											className="subtitle"
											variant={{
												type: 'copy',
												order: '1',
												style: 'normal',
											}}>
											{isLoggedIn
												? i18n('ngc.saveConfiguration.myAudi.subheadline.login')
												: i18n('ngc.saveConfiguration.myAudi.subheadline.logout')}
										</Text>
									</div>
								</Layout>
								{!isLoggedIn && (
									<Layout direction={'column'} rowGap={'var(--spacing-relative-sm)'}>
										{}
										<div tabIndex={0}>
											<Headline
												className="subtitle"
												margin={[0, 0, '8px', 0]}
												tag="h3"
												variant={{ type: 'headline', order: '4', weight: 'bold' }}>
												{i18n('ngc.saveConfiguration.myAudi.benefits.headline')}
											</Headline>
											<Text
												variant={{
													type: 'copy',
													order: '2',
													style: 'normal',
												}}>
												<span
													dangerouslySetInnerHTML={{
														__html: i18n('ngc.saveConfiguration.myAudi.benefits.content'),
													}}
												/>
											</Text>
										</div>
									</Layout>
								)}
							</Layout>
							<SaveConfiguration />
						</Layout>
					</StyledMyAudiWrapper>
				</>
			)}
		</StyledMainContainer>
	);
}
